/**
 * Import persistence (FR-IMP-09, FR-IMP-14 to FR-IMP-17).
 *
 * The plan is built in `core/import.ts`; this commits it.
 *
 * Everything created carries one `importBatchId`, which is what makes FR-IMP-15's
 * rollback a delete-by-field rather than a reconciliation exercise, and what lets the
 * `migrated` flag be derived rather than guessed.
 */

import {
  addDoc,
  collection,
  doc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import type { AccountTypeLookup } from '../core/rollups';
import { nestFields, rollupDeltas } from '../core/rollups';
import { balanceEffects, buildTransaction } from '../core/transactions';
import { CUTOVER } from '../core/seed';
import { UNASSIGNED_ACCOUNT, type ImportIssue, type ImportPlan } from '../core/import';
import { db } from './firebase';
import { trackWrite } from './syncState';

export class ImportError extends Error {}

/**
 * Transactions per batch.
 *
 * Each one is a document, and the account and rollup updates are aggregated across the
 * chunk into a handful more — so a chunk of 100 is roughly 120 operations, well inside
 * Firestore's 500 limit with room for a month boundary falling awkwardly.
 */
const CHUNK = 100;

export interface ImportRecord {
  id: string;
  fileName: string;
  fileHash: string;
  importedAt: Date | null;
  status: 'committed' | 'rolled-back';
  counts: { read: number; imported: number; flagged: number; skipped: number };
}

/** SHA-256 of the file's bytes (FR-IMP-14). */
export async function hashFile(file: Blob): Promise<string> {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Has this exact file been imported before?
 *
 * By content, not by name — renaming a file must not get it past this. FR-IMP-14 wants
 * the previous import's date in the message, so the whole record comes back.
 */
export async function findPreviousImport(fileHash: string): Promise<ImportRecord | null> {
  const snapshot = await getDocs(
    query(collection(db, 'imports'), where('fileHash', '==', fileHash), limit(1)),
  );
  if (snapshot.empty) return null;
  const d = snapshot.docs[0];
  const data = d.data();
  return {
    id: d.id,
    fileName: (data.fileName as string) ?? '',
    fileHash,
    importedAt: (data.importedAt as { toDate?: () => Date })?.toDate?.() ?? null,
    status: (data.status as ImportRecord['status']) ?? 'committed',
    counts: (data.counts as ImportRecord['counts']) ?? { read: 0, imported: 0, flagged: 0, skipped: 0 },
  };
}

export function watchImports(onChange: (imports: ImportRecord[]) => void): () => void {
  return onSnapshot(
    query(collection(db, 'imports'), orderBy('importedAt', 'desc'), limit(20)),
    (snapshot) =>
      onChange(
        snapshot.docs.map((d) => ({
          id: d.id,
          fileName: (d.data().fileName as string) ?? '',
          fileHash: (d.data().fileHash as string) ?? '',
          importedAt: (d.data().importedAt as { toDate?: () => Date })?.toDate?.() ?? null,
          status: (d.data().status as ImportRecord['status']) ?? 'committed',
          counts: (d.data().counts as ImportRecord['counts']) ?? { read: 0, imported: 0, flagged: 0, skipped: 0 },
        })),
      ),
    () => onChange([]),
  );
}

export interface CommitProgress {
  written: number;
  total: number;
}

/**
 * Write the plan.
 *
 * Account and rollup deltas are **aggregated per chunk** rather than written per
 * transaction. Six hundred rows touching the same dozen accounts would otherwise mean
 * six hundred writes to those dozen documents — and Firestore rejects two writes to one
 * document within a single batch, so per-transaction updates would not even be legal
 * here.
 */
export async function commitImport(
  plan: ImportPlan,
  fileName: string,
  fileHash: string,
  by: string,
  accountType: AccountTypeLookup,
  onProgress?: (progress: CommitProgress) => void,
): Promise<string> {
  const previous = await findPreviousImport(fileHash);
  if (previous && previous.status === 'committed') {
    const when = previous.importedAt?.toLocaleDateString() ?? 'earlier';
    throw new ImportError(
      `This exact file was already imported on ${when} as "${previous.fileName}". ` +
        'Roll that import back first if you want to redo it.',
    );
  }

  // `batch.update` fails outright if the document does not exist, so a single
  // account-less row would otherwise take the whole import down. Created on demand
  // rather than seeded, so the normal account list stays at 32.
  await ensureUnassignedAccount(plan);

  const importRef = await addDoc(collection(db, 'imports'), {
    fileName,
    fileHash,
    importedAt: serverTimestamp(),
    importedBy: by,
    status: 'committed',
    counts: {
      read: plan.summary.rowsRead,
      imported: plan.summary.importable,
      flagged: plan.summary.flagged,
      skipped: plan.summary.skipped,
    },
    mergeNotes: Object.entries(plan.summary.byRule).map(([rule, n]) => `${rule}: ${n} rows`),
  });
  const batchId = importRef.id;

  const rows = plan.transactions;
  for (let i = 0; i < rows.length; i += CHUNK) {
    const slice = rows.slice(i, i + CHUNK);
    const batch = writeBatch(db);

    const accountDeltas = new Map<string, { balancePaise: number; transactionCount: number }>();
    const rollupDeltasByMonth = new Map<string, Record<string, number>>();

    for (const planned of slice) {
      const ref = doc(collection(db, 'transactions'));
      const txn = buildTransaction(ref.id, { ...planned.input, importBatchId: batchId }, by);

      batch.set(ref, {
        ...stripId(txn),
        deletedAt: null,
        purgeAfter: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        importSource: { sheet: planned.sheet, rowNumber: planned.rowNumber },
        importIssues: planned.issues,
      });

      for (const effect of balanceEffects(txn, { cutover: CUTOVER })) {
        const current = accountDeltas.get(effect.accountId) ?? { balancePaise: 0, transactionCount: 0 };
        accountDeltas.set(effect.accountId, {
          balancePaise: current.balancePaise + effect.deltaPaise,
          transactionCount: current.transactionCount + 1,
        });
      }

      const fields = rollupDeltasByMonth.get(txn.monthKey) ?? {};
      for (const [path, value] of Object.entries(rollupDeltas(txn, accountType, 1))) {
        fields[path] = (fields[path] ?? 0) + value;
      }
      rollupDeltasByMonth.set(txn.monthKey, fields);
    }

    for (const [accountId, delta] of accountDeltas) {
      batch.update(doc(db, 'accounts', accountId), {
        balancePaise: increment(delta.balancePaise),
        transactionCount: increment(delta.transactionCount),
        updatedAt: serverTimestamp(),
      });
    }

    for (const [monthKey, fields] of rollupDeltasByMonth) {
      batch.set(
        doc(db, 'rollups', monthKey),
        { monthKey, updatedAt: serverTimestamp(), ...nestFields(fields, increment) },
        { merge: true },
      );
    }

    await trackWrite(batch.commit());
    onProgress?.({ written: Math.min(i + CHUNK, rows.length), total: rows.length });
  }

  // Issues go in last, so a failure part-way leaves no review queue pointing at
  // transactions that were never written.
  await writeIssues(batchId, plan.issues);

  return batchId;
}

async function ensureUnassignedAccount(plan: ImportPlan): Promise<void> {
  const needed = plan.transactions.some(
    (t) => t.input.accountId === UNASSIGNED_ACCOUNT || t.input.toAccountId === UNASSIGNED_ACCOUNT,
  );
  if (!needed) return;

  await trackWrite(
    setDoc(
      doc(db, 'accounts', UNASSIGNED_ACCOUNT),
      {
        name: 'Unassigned (from import)',
        type: 'bank',
        currency: 'INR',
        archived: false,
        openingBalancePaise: 0,
        openingBalanceDate: '2026-01-01',
        balancePaise: 0,
        minBalancePaise: null,
        sortOrder: 900,
        deleted: false,
        note: 'Holds imported rows whose source row had no account. Reassign, then archive.',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    ),
  );
}

async function writeIssues(batchId: string, issues: ImportIssue[]): Promise<void> {
  for (let i = 0; i < issues.length; i += 200) {
    const batch = writeBatch(db);
    for (const issue of issues.slice(i, i + 200)) {
      batch.set(doc(collection(db, 'imports', batchId, 'issues')), {
        ...issue,
        resolution: 'pending',
        createdAt: serverTimestamp(),
      });
    }
    await trackWrite(batch.commit());
  }
}

export interface ImportIssueRecord extends ImportIssue {
  id: string;
  resolution: 'pending' | 'corrected' | 'skipped';
}

export function watchImportIssues(
  batchId: string,
  onChange: (issues: ImportIssueRecord[]) => void,
): () => void {
  return onSnapshot(
    collection(db, 'imports', batchId, 'issues'),
    (snapshot) =>
      onChange(
        snapshot.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ImportIssueRecord, 'id'>) })),
      ),
    () => onChange([]),
  );
}

export async function resolveIssue(
  batchId: string,
  issueId: string,
  resolution: 'corrected' | 'skipped',
): Promise<void> {
  await trackWrite(
    updateDoc(doc(db, 'imports', batchId, 'issues', issueId), {
      resolution,
      resolvedAt: serverTimestamp(),
    }),
  );
}

/**
 * Undo an import (FR-IMP-15).
 *
 * Soft-deletes every transaction carrying the batch id and reverses its balance and
 * rollup effects. Soft, not hard: the rules deny hard delete to every client, and the
 * audit trail should show that an import happened and was undone rather than pretending
 * it never did.
 */
export async function rollbackImport(
  batchId: string,
  accountType: AccountTypeLookup,
  onProgress?: (progress: CommitProgress) => void,
): Promise<number> {
  const snapshot = await getDocs(
    query(collection(db, 'transactions'), where('importBatchId', '==', batchId)),
  );

  const { toTransactionList } = await import('./transactions');
  const transactions = toTransactionList(snapshot.docs).filter((t) => !t.deleted);

  for (let i = 0; i < transactions.length; i += CHUNK) {
    const slice = transactions.slice(i, i + CHUNK);
    const batch = writeBatch(db);

    const accountDeltas = new Map<string, { balancePaise: number; transactionCount: number }>();
    const rollupsByMonth = new Map<string, Record<string, number>>();

    for (const txn of slice) {
      batch.update(doc(db, 'transactions', txn.id), {
        deleted: true,
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      for (const effect of balanceEffects(txn, { cutover: CUTOVER })) {
        const current = accountDeltas.get(effect.accountId) ?? { balancePaise: 0, transactionCount: 0 };
        accountDeltas.set(effect.accountId, {
          balancePaise: current.balancePaise - effect.deltaPaise,
          transactionCount: current.transactionCount - 1,
        });
      }
      const fields = rollupsByMonth.get(txn.monthKey) ?? {};
      for (const [path, value] of Object.entries(rollupDeltas(txn, accountType, -1))) {
        fields[path] = (fields[path] ?? 0) + value;
      }
      rollupsByMonth.set(txn.monthKey, fields);
    }

    for (const [accountId, delta] of accountDeltas) {
      batch.update(doc(db, 'accounts', accountId), {
        balancePaise: increment(delta.balancePaise),
        transactionCount: increment(delta.transactionCount),
      });
    }
    for (const [monthKey, fields] of rollupsByMonth) {
      batch.set(
        doc(db, 'rollups', monthKey),
        { updatedAt: serverTimestamp(), ...nestFields(fields, increment) },
        { merge: true },
      );
    }

    await trackWrite(batch.commit());
    onProgress?.({ written: Math.min(i + CHUNK, transactions.length), total: transactions.length });
  }

  await trackWrite(
    updateDoc(doc(db, 'imports', batchId), { status: 'rolled-back', rolledBackAt: serverTimestamp() }),
  );

  return transactions.length;
}

function stripId<T extends { id: string }>(value: T): Omit<T, 'id'> {
  const { id: _id, ...rest } = value;
  return rest;
}
